/**
 * neptime.js — Nepal timezone utilities
 *
 * Nepal is UTC+5:45 (Asia/Kathmandu).
 * All timestamps from the API are stored as UTC on Vercel
 * (SQLite datetime('now','localtime') on a UTC server = UTC time).
 * We parse them as UTC and always display in Nepal time,
 * regardless of which device/country the app is opened from.
 */

import NepaliDateLib from 'nepali-date-converter';
const NepaliDate = NepaliDateLib.default || NepaliDateLib;

const TZ = 'Asia/Kathmandu';
const LOCALE = 'en-US'; // use en-US for consistent formatting engine across devices

/* ─── Calendar preference (Bikram Sambat ⇄ Gregorian) ───────────────
 * Dates are ALWAYS stored as Gregorian/UTC. This module converts to the
 * restaurant's chosen calendar at DISPLAY time only. The preference is a
 * module-level value set once after settings load (one session = one
 * restaurant), so the existing fmtDate/fmtDateTime call sites become
 * calendar-aware without threading a prop through every component. */
const BS_MONTHS = [
  'Baisakh', 'Jestha', 'Ashadh', 'Shrawan', 'Bhadra', 'Ashwin',
  'Kartik', 'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra',
];
let _calendar = 'nepali'; // 'nepali' (BS, default) | 'english' (Gregorian)
export function setCalendar(pref) { _calendar = pref === 'english' ? 'english' : 'nepali'; }
export function getCalendar() { return _calendar; }
export function isBS() { return _calendar === 'nepali'; }
export { BS_MONTHS };

/* Nepal-local Y-M-D for a timestamp, as numbers. We resolve the calendar
 * date in Nepal time first so AD→BS conversion is correct regardless of the
 * viewer's device timezone (avoids day-boundary errors). */
function nepalYMD(ts) {
  const d = parseUTC(ts);
  const [y, m, dd] = d.toLocaleDateString('en-CA', { timeZone: TZ }).split('-').map(Number);
  return { y, m, d: dd };
}
/* BS date for a timestamp. Feed local-noon of the Nepal AD date so the
 * library never rolls across a day boundary. */
export function toBS(ts) {
  const { y, m, d } = nepalYMD(ts);
  return new NepaliDate(new Date(y, m - 1, d, 12, 0, 0));
}

/* ─── BS ⇄ AD helpers for the date picker (display ⇄ stored value) ──── */
const _pad = (n) => String(n).padStart(2, '0');

/** BS parts {by, bm (0-11), bd} for an AD 'YYYY-MM-DD' string. */
export function adStrToBsParts(adStr) {
  const [y, m, d] = adStr.split('-').map(Number);
  const b = new NepaliDate(new Date(y, m - 1, d, 12, 0, 0));
  return { by: b.getYear(), bm: b.getMonth(), bd: b.getDate() };
}

/** AD 'YYYY-MM-DD' (local parts) for a BS date (bm is 0-11). */
export function bsToAdStr(by, bm, bd) {
  const ad = new NepaliDate(by, bm, bd).toJsDate();
  return `${ad.getFullYear()}-${_pad(ad.getMonth() + 1)}-${_pad(ad.getDate())}`;
}

/** Number of days in a BS month (bm 0-11), via BS→AD boundary diff. */
export function bsMonthDays(by, bm) {
  const start = new NepaliDate(by, bm, 1).toJsDate();
  const nByear = bm === 11 ? by + 1 : by;
  const nBmonth = bm === 11 ? 0 : bm + 1;
  const next = new NepaliDate(nByear, nBmonth, 1).toJsDate();
  return Math.round((next - start) / 86400000);
}

/** Today's BS parts {by, bm, bd}. */
export function todayBsParts() {
  const b = toBS(new Date().toISOString());
  return { by: b.getYear(), bm: b.getMonth(), bd: b.getDate() };
}

/**
 * Parse an API timestamp string as UTC.
 * API returns: "2026-04-24 09:21:00" (no timezone marker).
 * Appending 'Z' makes JS treat it as UTC instead of device-local time.
 */
export function parseUTC(ts) {
  if (!ts) return new Date();
  // Replace space with T and append Z → ISO 8601 UTC
  const iso = String(ts).replace(' ', 'T').replace(/Z?$/, 'Z');
  return new Date(iso);
}

/**
 * Today's date in Nepal as YYYY-MM-DD.
 * Use this wherever you need "today" for API date params.
 */
export function todayNPT() {
  return new Date().toLocaleDateString('en-CA', { timeZone: TZ });
}

/**
 * Yesterday's date in Nepal as YYYY-MM-DD.
 */
export function yesterdayNPT() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('en-CA', { timeZone: TZ });
}

/**
 * Start of current week (Monday) in Nepal as YYYY-MM-DD.
 */
export function weekStartNPT() {
  const now = new Date();
  // Get current day-of-week in Nepal
  const npDay = parseInt(
    now.toLocaleDateString('en-US', { timeZone: TZ, weekday: 'short' })
      .replace(/Sun/,'0').replace(/Mon/,'1').replace(/Tue/,'2')
      .replace(/Wed/,'3').replace(/Thu/,'4').replace(/Fri/,'5').replace(/Sat/,'6')
  );
  const day = isNaN(npDay) ? new Date().toLocaleDateString('en-US', { timeZone: TZ, weekday: 'numeric' }) : npDay;
  // Days since Monday (Mon=1..Sun=0 → offset)
  const dowMap = { Sun: 6, Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5 };
  const dowStr = now.toLocaleDateString('en-US', { timeZone: TZ, weekday: 'short' });
  const offset = dowMap[dowStr] ?? 0;
  const monday = new Date(now);
  monday.setDate(now.getDate() - offset);
  return monday.toLocaleDateString('en-CA', { timeZone: TZ });
}

/**
 * Start of current month in Nepal as YYYY-MM-DD.
 */
export function monthStartNPT() {
  const now = new Date();
  const [year, month] = now.toLocaleDateString('en-CA', { timeZone: TZ }).split('-');
  return `${year}-${month}-01`;
}

/**
 * Start of current year in Nepal as YYYY-MM-DD.
 */
export function yearStartNPT() {
  const year = new Date().toLocaleDateString('en-CA', { timeZone: TZ }).split('-')[0];
  return `${year}-01-01`;
}

/**
 * Format: "Apr 24" or "Apr 24, 2026"
 */
export function fmtDate(ts, includeYear = false) {
  if (isBS()) {
    const b = toBS(ts);
    const base = `${BS_MONTHS[b.getMonth()]} ${b.getDate()}`;
    return includeYear ? `${base}, ${b.getYear()}` : base;
  }
  const d = parseUTC(ts);
  const opts = { timeZone: TZ, day: 'numeric', month: 'short' };
  if (includeYear) opts.year = 'numeric';
  return d.toLocaleDateString(LOCALE, opts);
}

/**
 * Format: "09:21 PM"
 */
export function fmtTime(ts) {
  return parseUTC(ts).toLocaleTimeString(LOCALE, {
    timeZone: TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Format: "Apr 24 · 09:21 PM"
 */
export function fmtDateTime(ts) {
  return `${fmtDate(ts)} · ${fmtTime(ts)}`;
}

/**
 * Format: "Apr 24, 2026 · 09:21 PM"
 */
export function fmtDateTimeYear(ts) {
  return `${fmtDate(ts, true)} · ${fmtTime(ts)}`;
}
